import React, { useEffect, useState } from "react"
import moment from "moment"
import { Badge } from "../ui/badge"
import { getOrdinal } from "../../utils/get-ordinal"
import { MeetingRequests } from "../../api/app.service"

interface Props {
  service: any
  coachPublicProfileData: any
}

const GroupDates: React.FC<Props> = ({ service, coachPublicProfileData }) => {
  const { service_details, is_group_service } = service || {}
  const [sortedDates, setSortedDates] = useState<Date[]>([])

  const halfwayIndex = Math.ceil(sortedDates.length / 2)
  const leftColumnItems = sortedDates.slice(0, halfwayIndex)
  const rightColumnItems = sortedDates.slice(halfwayIndex)

  const today = new Date()
  const timeZoneAbbr = today
    .toLocaleTimeString("en-US", { timeZoneName: "short" })
    .split(" ")
    .pop()

  const getStartDatesWithVariants = () => {
    const meetingDates = service_details?.group?.meetingDates || []
    if (meetingDates.length === 0) return []

    return meetingDates.map((date: string, index: number) => {
      const isPast = new Date(date) < new Date()
      const variant = isPast ? "destructive" : "success"
      const sessionLabel = getOrdinal(index + 1)
      const formattedDate = moment(date).format("MMM DD [at] h:mm A")
      const sessionStatus = isPast ? "was" : "Scheduled for"

      return { message: `${sessionLabel} Session ${sessionStatus} ${formattedDate}`, variant }
    })
  }

  useEffect(() => {
    const formatAndSet = (dates: string[]) => {
      const parsedDates = dates
        .map((d) => new Date(d))
        .sort((a, b) => a.getTime() - b.getTime())
      setSortedDates(parsedDates)
    }

    const fetchAndSetDates = () => {
      if (service_details?.type === "recurring") {
        MeetingRequests.getRecurringMeetings({
          user_id: coachPublicProfileData?.user_id,
          stripe_connect_product_id: service?.stripe_product_id,
        })
          .then((response: { meetings: { start_time: string }[] }) => {
            const times = (response.meetings || []).map((m) => m.start_time)
            formatAndSet(times)
          })
          .catch((error) => {
            console.error("Failed to fetch recurring meetings:", error)
          })
      } else if (service_details?.group?.meetingDates) {
        formatAndSet(service_details.group.meetingDates)
      } else {
        setSortedDates([])
      }
    }

    fetchAndSetDates()
    window.addEventListener("resize", fetchAndSetDates)
    return () => window.removeEventListener("resize", fetchAndSetDates)
  }, [
    service_details?.type,
    service_details?.group?.meetingDates,
    coachPublicProfileData?.user_id,
    service?.stripe_product_id,
  ])

  return (
    <>
      <div className="w-full mb-4">
        {is_group_service && service_details?.type ? (
          <div className="w-full">
            <div className="flex flex-row sm:flex-row md:flex-col lg:flex-row gap-2 mb-4">
              <div className="bg-grayFlash flex h-8 w-[142px] items-center justify-center rounded-[8px] font-bold">
                Scheduled Dates
              </div>
              <div className="px-2 bg-grayFlash h-8 w-max flex items-center justify-center rounded-[8px] italic mt-0 md:mt-2 lg:mt-0">
                All times shown in {timeZoneAbbr}
              </div>
            </div>

            <div
              className="grid grid-cols-2 gap-2"
              style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
            >
              <style>
                {`
                  @media (min-width: 768px) and (max-width: 950px) {
                    .grid-cols-2 { grid-template-columns: 1fr !important; }
                  }
                `}
              </style>

              <div className="flex flex-col items-start gap-2">
                {leftColumnItems.map((date, idx) => {
                  const isPast = moment(date).isBefore(moment())
                  return (
                    <Badge
                      key={`left-${idx}`}
                      variant="neutral"
                      className={`w-auto ${isPast ? "line-through text-gray-400" : ""}`}
                    >
                      {moment(date).format("MMM DD [at] h:mm A")}
                    </Badge>
                  )
                })}
              </div>

              <div className="flex flex-col items-start gap-2">
                {rightColumnItems.map((date, idx) => {
                  const isPast = moment(date).isBefore(moment())
                  return (
                    <Badge
                      key={`right-${idx}`}
                      variant="neutral"
                      className={`w-auto ${isPast ? "line-through text-gray-400" : ""}`}
                    >
                      {moment(date).format("MMM DD [at] h:mm A")}
                    </Badge>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
              <div className="bg-grayFlash flex h-8 w-[142px] text-center items-center justify-center rounded-[8px] font-bold">
                Scheduled Dates
              </div>
              <div className="px-2 bg-grayFlash h-8 w-max flex text-center items-center justify-center rounded-[8px] italic mt-2 sm:mt-0">
                All times shown in {timeZoneAbbr}
              </div>
            </div>

            {getStartDatesWithVariants().map((start: any, index: any) => {
              const isPast = start.variant === "destructive"
              return (
                <div key={index} className="flex justify-start mb-2">
                  <Badge
                    variant="neutral"
                    className={`w-auto ${isPast ? "line-through text-gray-400" : ""}`}
                  >
                    {start.message}
                  </Badge>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="bg-grayMist mb-[16px] h-[1px] w-full px-[20px]"></div>
    </>
  )
}

export default GroupDates